import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useRef } from 'react'
import CountUp from 'react-countup'
import useIsMountedRef from 'use-is-mounted-ref'
import { SelfProvisioning as SelfProvisioningLanguage } from '../../../../i18n'
import { DEVICE_FILTER_EXTENSIONS_ONLY } from '../../../NonReact/constants/DeviceTypes'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import Icon, { IconName } from '../../Common/Components/Icon'
import Loader from '../../Common/Components/Loader'
import { LanguageContext } from '../../Common/Contexts'
import { FooterActions } from '../Actions/FooterActions'
import { EntityCreateStatuses } from '../Constants/Constants'
import { SelfProvisioningConfig, SelfProvisioningConfigContext } from '../Contexts/SelfProvisioningConfigContext'
import { SelfProvisioningContext, SelfProvisioningContextType } from '../Contexts/SelfProvisioningContext'
import { EntityCreateStatus } from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { getDeviceCount } from '../../../NonReact/common/API/EntityAPIClient'

/**
 * Provisioning wait screen - Screen user is show while waiting for provisioning to complete.
 */
export const ProvisioningWaitScreen: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const {
        api: { heartbeatPeriodMillisecs, maxSyncedDevicesChecks }
    }: SelfProvisioningConfig = useContext(SelfProvisioningConfigContext)
    const {
        telSysState: [telSysID],
        telSysStatus: [telSysStatus, setTelSysStatus],
        partitionState: [partitionID],
        partitionStatus: [partitionStatus, , getPartitionStatus],
        deviceCountState: [deviceCount, setDeviceCount],
        nextStep,
        reset
    }: SelfProvisioningContextType = useContext(SelfProvisioningContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const isAllSynced: boolean =
        telSysStatus === EntityCreateStatuses.SYNCED && partitionStatus === EntityCreateStatuses.SYNCED

    const isTelSysSyncCompleteOrFailed: boolean =
        telSysStatus === EntityCreateStatuses.SYNCED || telSysStatus === EntityCreateStatuses.FAILED
    const isPartitionSyncCompleteOrFailed: boolean =
        partitionStatus === EntityCreateStatuses.SYNCED || partitionStatus === EntityCreateStatuses.FAILED

    const isCheckingDeviceStatus: MutableRefObject<boolean> = useRef(false)

    useEffect((): void => {
        if (
            !isCheckingDeviceStatus.current &&
            partitionStatus === EntityCreateStatuses.CREATED &&
            !IsVarEmpty(telSysID) &&
            !IsVarEmpty(partitionID)
        ) {
            isCheckingDeviceStatus.current = true

            const checkDeviceStatus = (syncedChecks: number): void => {
                // Once the partition is synced check for devices until we hit max checks.
                if (syncedChecks >= maxSyncedDevicesChecks) return undefined

                // When the partition status is created or synced check for devices.
                getPartitionStatus().then((partStatus: Nullable<EntityCreateStatus>): void => {
                    if (
                        (partStatus === EntityCreateStatuses.CREATED || partStatus === EntityCreateStatuses.SYNCED) &&
                        isMounted.current
                    ) {
                        // Get device count from the server.
                        getDeviceCount(telSysID ?? 0, partitionID ?? 0, DEVICE_FILTER_EXTENSIONS_ONLY).then(
                            (noDevices: Nullable<number>): void => {
                                if (isMounted.current) {
                                    setDeviceCount(noDevices)
                                    setTimeout(
                                        (): void =>
                                            checkDeviceStatus(
                                                partStatus === EntityCreateStatuses.SYNCED
                                                    ? syncedChecks + 1
                                                    : syncedChecks
                                            ),
                                        heartbeatPeriodMillisecs
                                    )

                                    // When we start getting devices back from the server the tel server status is assumed to be synced.
                                    if (noDevices && noDevices > 0 && telSysStatus !== EntityCreateStatuses.SYNCED) {
                                        setTelSysStatus(EntityCreateStatuses.SYNCED)
                                    }
                                }
                            }
                        )
                    }
                })

                return undefined
            }

            checkDeviceStatus(0)
        }
    }, [partitionStatus, telSysStatus])

    return (
        <div className='provisioning-wait-screen'>
            <div className='statuses-container'>
                <div className={`status-container status-${telSysStatus?.toLowerCase()}`}>
                    <div title={language.get(SelfProvisioningLanguage.SelfProvisioning_EntityTS_Label)}>
                        <Icon iconName={IconName.TELSERVER} />
                    </div>
                    <div className='status-message'>
                        <div className='entity-title'>
                            {language.get(SelfProvisioningLanguage.SelfProvisioning_EntityTS_Label)}
                        </div>
                        <div className='entity-id-container'>
                            <div className='id-label'>
                                {language.get(SelfProvisioningLanguage.SelfProvisioning_ID_Label)}
                            </div>
                            <div className='entity-id'>{IsVarEmpty(telSysID) ? '?' : telSysID}</div>
                        </div>

                        <div className='status-description'>
                            {language.getInterpolated(
                                `SelfProvisioning_StatusMessage_TelSys_${telSysStatus}`,
                                telSysID
                            )}
                        </div>
                    </div>
                    {isTelSysSyncCompleteOrFailed ? (
                        <span
                            title={language.get(SelfProvisioningLanguage.SelfProvisioning_EntityTS_Label)}
                            className='completed-circle'
                        />
                    ) : (
                        <Loader size={160} />
                    )}
                </div>

                <div className={`status-container status-${partitionStatus?.toLowerCase()}`}>
                    <div title={language.get(SelfProvisioningLanguage.SelfProvisioning_EntityP_Label)}>
                        <Icon iconName={IconName.PARTITION} />
                    </div>
                    <div className='status-message'>
                        <div className='entity-title'>
                            {language.get(SelfProvisioningLanguage.SelfProvisioning_EntityP_Label)}
                        </div>
                        <div className='entity-id-container'>
                            <div className='id-label'>
                                {language.get(SelfProvisioningLanguage.SelfProvisioning_No_Label)}
                            </div>
                            <div className='entity-id'>{IsVarEmpty(partitionID) ? '?' : partitionID}</div>
                        </div>

                        <div className='status-description'>
                            {language.getInterpolated(
                                `SelfProvisioning_StatusMessage_Partition_${partitionStatus}`,
                                partitionID
                            )}
                        </div>
                    </div>
                    {isPartitionSyncCompleteOrFailed ? (
                        <span
                            title={language.get(SelfProvisioningLanguage.SelfProvisioning_EntityTS_Label)}
                            className='completed-circle'
                        />
                    ) : (
                        <Loader size={160} />
                    )}
                </div>
            </div>

            {!IsVarEmpty(deviceCount) &&
            deviceCount > 0 &&
            (isPartitionSyncCompleteOrFailed || partitionStatus === EntityCreateStatuses.CREATED) ? (
                <div className='sync-summary-container'>
                    <div className='sync-summary'>
                        <Icon iconName={IconName.PHONE} />
                        <div className='num-devices-container'>
                            <div className='devices-label'>
                                {language.get(SelfProvisioningLanguage.SelfProvisioning_SyncSumary_Info)}
                            </div>
                            <div className='device-count-container'>
                                <div className='device-count-title'>
                                    {language.get(SelfProvisioningLanguage.SelfProvisioning_Count_Label)}
                                </div>
                                <div className='num-devices'>
                                    <CountUp preserveValue duration={1} end={deviceCount} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <></>
            )}

            {telSysStatus === EntityCreateStatuses.FAILED || partitionStatus === EntityCreateStatuses.FAILED ? (
                <div className='pws-error-message'>
                    {language.get(SelfProvisioningLanguage.SelfProvisioning_Error_SyncFailed)}
                </div>
            ) : (
                <></>
            )}

            {isAllSynced ? (
                <FooterActions
                    showRightSideButton={isAllSynced}
                    rightSideButtonType='button'
                    rightSideButtonClick={nextStep}
                    rightSideButtonLabel={language.get(SelfProvisioningLanguage.SelfProvisioning_FooterActions_Next)}
                />
            ) : (
                <FooterActions
                    showRightSideButton={
                        telSysStatus === EntityCreateStatuses.FAILED || partitionStatus === EntityCreateStatuses.FAILED
                    }
                    rightSideButtonType='button'
                    rightSideButtonClick={reset}
                    rightSideButtonLabel={language.get(SelfProvisioningLanguage.SelfProvisioning_FooterActions_Back)}
                />
            )}
        </div>
    )
}
